import { type Objectish, produce } from "immer";
import { useCallback } from "react";
import {
	type Actor,
	type AnyActorLogic,
	assign,
	createActor,
	fromPromise,
	setup,
	waitFor,
} from "xstate";

import { type DoTransactionFn } from "@moment/api-collab/metadata-state";
import { PageStorageSubscribableBridge } from "@moment/api-collab/page-local-storage/client";

import { isDesktopAppMode } from "~/auth/interop/common";
import {
	getDocumentDirPath,
	getPageStorageFilePath,
	readJsonFile,
	writeJsonFile,
} from "~/data/filesystem";

import { coerceToError } from "../coerceToError";

interface PageStorageContext {
	workspaceName: string;
	documentId: string;
	pageId: string;
	data: Objectish | null;
	dirty: boolean;
	error: Error | null;
	loading: boolean;
}

type PageStorageEvent = { type: "FETCH" } | { type: "UPDATE"; data: Objectish };

export const pageStorageMachine = setup({
	types: {} as {
		context: PageStorageContext;
		events: PageStorageEvent;
		input: {
			workspaceName: string;
			documentId: string;
			pageId: string;
		};
	},

	actors: {
		fetchInitialPageStorageState: fromPromise(
			async ({
				input,
			}: {
				input: { workspaceName: string; documentId: string; pageId: string };
			}) => {
				if (!isDesktopAppMode()) {
					const path = `${input.workspaceName}/${input.documentId}/pages/${input.pageId}`;
					const response = await fetch(`/api/public/${path}/pageStorage.json`);
					if (!response.ok) {
						throw new Error("Failed to fetch public page storage");
					}
					return await response.json();
				}

				// IMPORTANT: Zero value is `{}`. If `pageStorage` returns `undefined` (as it does
				// in the error case), then all React compute cells hang indefinitely. The runtime
				// interprets `undefined` as "a result has not been produced yet."
				const documentPath = await getDocumentDirPath(input.documentId);
				const path = await getPageStorageFilePath(documentPath, input.pageId);
				const ret = (await readJsonFile(path)) ?? {};
				return ret;
			}
		),
		writePageStorageFile: fromPromise(
			async ({
				input,
			}: {
				input: {
					workspaceName: string;
					documentId: string;
					pageId: string;
					data: Objectish | null;
				};
			}) => {
				const documentPath = await getDocumentDirPath(input.documentId);
				const path = await getPageStorageFilePath(documentPath, input.pageId);
				await writeJsonFile(path, input.data ?? {});
			}
		),
	},
	actions: {
		clearDirty: assign({ dirty: false }),
		setLoading: assign({
			data: null,
			error: null,
			loading: true,
			dirty: false,
		}),
		clearAll: assign({
			data: null,
			error: null,
			loading: false,
			dirty: false,
		}),
		setError: assign({
			data: null,
			error: ({ event }: { event: PageStorageEvent }) =>
				"error" in event ? coerceToError(event.error) : null,
			loading: false,
			dirty: false,
		}),
		queueWrite: assign({
			data: ({ context, event }) =>
				"data" in event ? { ...context.data, ...event.data } : context.data,
			dirty: true,
		}),
	},
}).createMachine({
	id: "pageStorage",
	initial: "loading",
	context: ({ input }) => ({
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		pageId: input.pageId,
		data: {},
		dirty: false,
		error: null,
		loading: true,
	}),
	states: {
		loading: {
			entry: ["setLoading"],
			invoke: {
				src: "fetchInitialPageStorageState",
				input: ({ context }) => ({
					workspaceName: context.workspaceName,
					documentId: context.documentId,
					pageId: context.pageId,
				}),
				onDone: {
					target: "ready",
					actions: ["clearAll", assign({ data: ({ event }) => event.output })],
				},
				onError: { target: "#pageStorage.error", actions: ["setError"] },
			},
		},
		ready: {
			initial: "idle",
			states: {
				idle: {
					always: {
						guard: ({ context }) => context.dirty,
						target: "saving",
						// IMPORTANT: Do this BEFORE the write, so that if a change comes in as
						// we're writing, we can queue up another write.
						actions: ["clearDirty"],
					},
				},
				saving: {
					invoke: {
						src: "writePageStorageFile",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							pageId: context.pageId,
							data: context.data,
						}),
						onDone: { target: "idle" },
						onError: { target: "#pageStorage.error", actions: ["setError"] },
					},
				},
			},
			on: {
				FETCH: "loading",
				UPDATE: { actions: "queueWrite" },
			},
		},
		error: { on: { FETCH: "loading" } },
	},
});

const bridges = new Map<string, PageStorageSubscribableBridge>();
const actors = new Map<string, Actor<AnyActorLogic>>();

const makeKey = (workspaceName: string, documentId: string, pageId: string) =>
	`${workspaceName}:${documentId}:${pageId}`;

export const usePageStorage = (input: {
	workspaceName: string;
	documentId: string;
	pageId: string;
}) => {
	const key = makeKey(input.workspaceName, input.documentId, input.pageId);

	let actor = actors.get(key);
	if (!actor) {
		actor = createActor(pageStorageMachine, { input });
		actor.start();
		actors.set(key, actor);
	}

	const setPageStorage = useCallback(
		async (doTransaction: DoTransactionFn<Objectish>) => {
			const data = produce<Objectish>(actor.getSnapshot().context.data ?? {}, doTransaction);
			actor.send({ type: "UPDATE", data });
			await waitFor(actor, (state) => !state.matches({ ready: "saving" }));
		},
		[actor]
	);

	let pageStorageBridge = bridges.get(key);
	if (!pageStorageBridge) {
		pageStorageBridge = new PageStorageSubscribableBridge({
			subscribe: (onUpdate) =>
				actor.subscribe((snap) => {
					onUpdate(snap.context.data ?? {});
				}),
			commit: setPageStorage,
		});
		bridges.set(key, pageStorageBridge);
	}

	return { pageStorageBridge };
};
